import React, { useMemo, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Snackbar,
    Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { quizQuestions, resultTiers } from "./../../data/QuizData";
import Section from "../../components/layout/Section";

export default function NCQuiz2026() {
    const theme = useTheme();

    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [score, setScore] = useState(0);

    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const answeredCount = Object.keys(answers).length;

    const allAnswered =
        answeredCount === quizQuestions.length;

    const resultTier = useMemo(() => {
        return resultTiers.find(
            tier =>
                score >= tier.minScore &&
                score <= tier.maxScore
        );
    }, [score]);

    const handleSelect = (questionId, optionId) => {
        if (submitted) return;

        setAnswers(prev => ({
            ...prev,
            [questionId]: optionId
        }));
    };

    const handleSubmit = () => {
        let totalCorrect = 0;

        quizQuestions.forEach(question => {
            if (
                answers[question.id] ===
                question.correctAnswer
            ) {
                totalCorrect++;
            }
        });

        setScore(totalCorrect);
        setSubmitted(true);
    };

    const handleRetry = () => {
        setAnswers({});
        setSubmitted(false);
        setScore(0);
    };

    const handleShare = async () => {
        try {
            const text =
                `I got "${resultTier.title}" on Stylisher's anniversary NC quiz! ` +
                `Test your knowledge at https://stylisher.club/articles/anniversary-nc-quiz`;

            await navigator.clipboard.writeText(text);

            setSnackbarOpen(true);
        } catch (err) {
            console.error(err);
        }
    };

    const getOptionBorder = (
        question,
        optionId
    ) => {
        const selected =
            answers[question.id];

        if (!submitted) {
            return selected === optionId
                ? "3px solid #FFD700"
                : "3px solid rgba(255,255,255,0.2)";
        }

        const isCorrect =
            optionId === question.correctAnswer;

        const isSelected =
            selected === optionId;

        if (isCorrect) {
            return "3px solid #4CAF50";
        }

        if (
            isSelected &&
            !isCorrect
        ) {
            return "3px solid #F44336";
        }

        return "3px solid rgba(255,255,255,0.2)";
    };

    return (
        <Box sx={{ p: 2.5 }}>
            <Section id="intro" title="Anniversary NC Quiz">
                <Typography
                    variant="caption"
                    color="secondary"
                    sx={{ mt: -1, mb: 2, display: "block" }}
                >
                    Last updated: June 2026
                </Typography>
            </Section>

            <Typography
                variant="body1"
                sx={{ mb: 3 }}
            >
                In honor of our first anniversary, we've made a NC quiz. We're showcasing some our stats in this quiz format, so take your best guesses and have fun with this! This quiz data is taken specifically from June 23, 2025 (our launch date!) to June 7, 2026 (our cutoff date) so keep that in mind as you're answering the questions!
            </Typography>

            <Paper
                sx={{
                    p: 2,
                    mb: 4,
                    position: "sticky",
                    top: 10,
                    zIndex: 5
                }}
            >
                {!submitted ? (
                    <Typography variant="h6">
                        Answered: {answeredCount} /{" "}
                        {quizQuestions.length}
                    </Typography>
                ) : (
                    <Typography variant="h6">
                        Score: {score} /{" "}
                        {quizQuestions.length}
                    </Typography>
                )}
            </Paper>

            {quizQuestions.map(question => (
                <Paper
                    key={question.id}
                    sx={{
                        p: 3,
                        mb: 4
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            color: theme.palette.accent.main,
                            marginBottom: 2
                        }}
                    >
                        {question.id}.{" "}
                        {question.question}
                    </Typography>

                    <Grid
                        container
                        spacing={3}
                        justifyContent="center"

                    >
                        {question.options.map(
                            option => (
                                <Grid
                                    item
                                    xs={6}
                                    md={3}
                                    key={option.id}
                                    p={2}
                                >
                                    <Card
                                        onClick={() =>
                                            handleSelect(
                                                question.id,
                                                option.id
                                            )
                                        }
                                        sx={{
                                            cursor: submitted
                                                ? "default"
                                                : "pointer",
                                            border: getOptionBorder(
                                                question,
                                                option.id
                                            ),
                                            maxWidth: '150',
                                            height: '100%',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                p: 1
                                            }}
                                        >
                                            {option.image ?
                                                <Box
                                                    component="img"
                                                    src={option.image}
                                                    alt={
                                                        option.name
                                                    }
                                                    sx={{
                                                        maxWidth:
                                                            "100%",
                                                        maxHeight:
                                                            "100%",
                                                        objectFit:
                                                            "contain"
                                                    }}
                                                    paddingTop={2}
                                                /> :
                                                ""}
                                        </Box>

                                        <CardContent>
                                            <Typography
                                                align="center"
                                                maxWidth={150}
                                            >
                                                {option.name}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )
                        )}
                    </Grid>

                    {submitted && (
                        <Alert
                            severity={
                                answers[
                                    question.id
                                ] ===
                                    question.correctAnswer
                                    ? "success"
                                    : "error"
                            }
                            sx={{ mt: 3 }}
                        >
                            <Typography>
                                {
                                    question.explanation
                                }
                            </Typography>
                        </Alert>
                    )}
                </Paper>
            ))}

            {!submitted ? (
                <Button
                    variant="contained"
                    size="large"
                    disabled={!allAnswered}
                    onClick={handleSubmit}
                    fullWidth
                >
                    Submit Quiz
                </Button>
            ) : (
                <Paper
                    sx={{
                        mt: 4,
                        p: 4,
                        textAlign: "center"
                    }}
                >
                    <Typography
                        variant="h4"
                        gutterBottom                        
                        sx={{
                            color: theme.palette.accent.main,
                        }}
                    >
                        Your Result
                    </Typography>

                    <Typography
                        variant="h5"
                        gutterBottom
                    >
                        {resultTier.title}
                    </Typography>

                    <Box
                        component="img"
                        src={resultTier.image}
                        alt={
                            resultTier.title
                        }
                        sx={{
                            width: "100%",
                            maxWidth: 500,
                            mb: 2
                        }}
                    />

                    <Typography
                        sx={{ mb: 3 }}
                    >
                        {
                            resultTier.description
                        }
                    </Typography>

                    <Typography
                        variant="h6"
                        sx={{ mb: 3 }}
                    >
                        Score: {score} /{" "}
                        {quizQuestions.length}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent:
                                "center",
                            flexWrap: "wrap"
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={
                                handleShare
                            }
                        >
                            Share Result
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={
                                handleRetry
                            }
                        >
                            Try Again
                        </Button>
                    </Box>
                </Paper>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() =>
                    setSnackbarOpen(false)
                }
            >
                <Alert severity="success">
                    Result copied to clipboard!
                </Alert>
            </Snackbar>
        </Box>
    );
}